import { useState } from 'react';
import { useQuery, useQueryClient, UseQueryOptions } from 'react-query';
import { isEmpty } from 'src/validations';
import apiClient from '../apiClient';
import { ApiResponseType, PaginationResponseType, responseWrapper } from '../helpers';
import { API_QUERIES } from '../keys';
import { FinancialProject } from './types';
import { GetFinancialProjectsParams } from './useGetFinancialProjects';

export function useGetAllFinancialProjects(
  options?: UseQueryOptions<
    ApiResponseType<PaginationResponseType<FinancialProject>>,
    Error,
    PaginationResponseType<FinancialProject>
  >
) {
  const [params, setParams] = useState<GetFinancialProjectsParams>(null);
  const {
    data: allFinancialProjectsResponse,
    error,
    isError,
    isFetching: isLoading,
    refetch: onGetAllFinancialProjects,
  } = useQuery<
    ApiResponseType<PaginationResponseType<FinancialProject>>,
    Error,
    PaginationResponseType<FinancialProject>
  >([API_QUERIES.GET_ALL_FINANCIAL_PROJECTS, { ...params, take: 10000 }], {
    queryFn: (query) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [_, ...params] = query.queryKey;
      return responseWrapper<ApiResponseType<PaginationResponseType<FinancialProject>>>(
        apiClient.getFinancialProjects,
        params
      );
    },
    notifyOnChangeProps: ['data', 'isFetching'],
    keepPreviousData: true,
    enabled: !isEmpty(params),
    ...options,
  });

  const {
    data: allFinancialProjects,
    hasNext,
    payloadSize,
    totalRecords,
  } = allFinancialProjectsResponse || {};

  const queryClient = useQueryClient();

  const handleInvalidateAllFinancialProjects = () =>
    queryClient.invalidateQueries(API_QUERIES.GET_ALL_FINANCIAL_PROJECTS);

  return {
    allFinancialProjects,
    hasNext,
    payloadSize,
    totalRecords,
    error,
    isError,
    isLoading,
    currentParams: params,
    onGetAllFinancialProjects,
    setParams,
    handleInvalidateAllFinancialProjects,
  };
}
